import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useLots } from '../hooks/useLots.js'
import LotCard from '../components/LotCard.jsx'
import Loader from '../components/Loader.jsx'
import ErrorState from '../components/ErrorState.jsx'
import EmptyState from '../components/EmptyState.jsx'
import { CATEGORIES } from '../data/constants.js'

// JSON-LD estructurado para la página de inicio
const siteJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Zuchlag',
  url: 'https://zuchlag.de/',
  description: 'Zuchlag ist ein Such- und Vergleichsportal für Industrieauktionen in ganz Europa.'
}

export default function HomePage() {
  // "Endet bald": solo activos, cierre futuro, ordenados por cierre próximo (requisito 3)
  const { data, pagination, loading, error, refetch } = useLots({ status: 'active', order: 'closing', per_page: 12 })
  // "Neu eingestellt": lotes activos añadidos recientemente
  const recent = useLots({ status: 'active', order: 'newest', per_page: 8 })

  useEffect(() => {
    document.title = 'Zuchlag - Industrieauktionen aus ganz Europa an einem Ort'
    document.querySelector('link[rel="canonical"]')?.setAttribute('href', 'https://zuchlag.de/')

    // Inyectar/actualizar JSON-LD del sitio
    let script = document.getElementById('jsonld-site')
    if (!script) {
      script = document.createElement('script')
      script.type = 'application/ld+json'
      script.id = 'jsonld-site'
      document.head.appendChild(script)
    }
    script.textContent = JSON.stringify(siteJsonLd)
  }, [])

  return (
    <div className="page-home">
      {/* Hero */}
      <section className="hero">
        <div className="container hero-inner">
          <h1 className="hero-title">Industrieauktionen aus ganz Europa an einem Ort</h1>
          <p className="hero-sub">
            Durchsuchen Sie gebrauchte Maschinen, Nutzfahrzeuge und Industrieanlagen aus
            verifizierten Auktionen. Bieten Sie direkt auf der Original-Plattform.
          </p>

          <form className="hero-search" action="/suche" role="search">
            <input
              type="search"
              name="q"
              placeholder="Wonach suchen Sie? z. B. Stapler, Bagger, CNC…"
              aria-label="Suche nach Auktionen"
            />
            <button type="submit" className="btn btn-primary btn-lg">Suchen</button>
          </form>

          <div className="hero-cats">
            {CATEGORIES.map((c) => (
              <Link key={c.key} className="hero-cat" to={`/suche?category=${c.key}`}>{c.label}</Link>
            ))}
          </div>
        </div>
      </section>

      {/* Últimos lotes / próximos a cerrar */}
      <section className="container section">
        <div className="section-head">
          <h2>Endet bald</h2>
          <Link to="/suche" className="section-more">Alle Auktionen ansehen →</Link>
        </div>

        {loading && <Loader />}
        {error && <ErrorState message={error} onRetry={refetch} />}
        {!loading && !error && data.length === 0 && (
          <EmptyState message="Derzeit sind keine Auktionen verfügbar. Schauen Sie später wieder vorbei." />
        )}
        {!loading && !error && data.length > 0 && (
          <>
            <div className="lot-grid">
              {data.map((lot) => <LotCard key={lot.id} lot={lot} />)}
            </div>
            {(pagination?.total || 0) > 12 && (
              <div className="section-more-btn">
                <Link to="/suche" className="btn btn-outline">Alle {pagination.total} Auktionen ansehen</Link>
              </div>
            )}
          </>
        )}
      </section>

      {/* Nuevos lotes activos (requisito 3) */}
      <section className="container section">
        <div className="section-head">
          <h2>Neu eingestellt</h2>
          <Link to="/suche" className="section-more">Alle Auktionen ansehen →</Link>
        </div>
        {recent.loading && <Loader />}
        {recent.error && <ErrorState message={recent.error} onRetry={recent.refetch} />}
        {!recent.loading && !recent.error && recent.data.length === 0 && (
          <EmptyState message="Derzeit sind keine neuen Auktionen verfügbar." />
        )}
        {!recent.loading && !recent.error && recent.data.length > 0 && (
          <div className="lot-grid">
            {recent.data.map((lot) => <LotCard key={lot.id} lot={lot} />)}
          </div>
        )}
      </section>

      {/* Cómo funciona (agregador) */}
      <section className="container section howitworks">
        <h2>So funktioniert Zuchlag</h2>
        <div className="hiw-grid">
          <div className="hiw-card">
            <div className="hiw-num">1</div>
            <h3>Durchsuchen</h3>
            <p>Finden Sie gebrauchte Maschinen und Anlagen aus Auktionen mehrerer Häuser – an einem Ort.</p>
          </div>
          <div className="hiw-card">
            <div className="hiw-num">2</div>
            <h3>Vergleichen</h3>
            <p>Vergleichen Sie Preis, Zustand, Standort und Enddatum der Gebote.</p>
          </div>
          <div className="hiw-card">
            <div className="hiw-num">3</div>
            <h3>Auf der Original-Plattform bieten</h3>
            <p>Gebote geben Sie direkt beim jeweiligen Auktionshaus. Zuchlag leitet Sie zum Original-Angebot.</p>
          </div>
        </div>
      </section>
    </div>
  )
}
